import type { FC, FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { Check, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/generals/button'
import { OptionGrid, TextField } from '@/components/widgets/quote-form-parts'
import { contactLimits, contactTopics } from '@/constants/contact'
import { siteConfig } from '@/constants/site'

type Status = 'idle' | 'submitting' | 'done' | 'error'

export const ContactForm: FC = (): ReactNode => {
    const messageId = useId()
    const topicId = useId()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [topic, setTopic] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<Status>('idle')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found: Record<string, string> = {}

        if (!name.trim()) found.name = "Please tell us your name."
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) found.email = "Please enter an email we can reply to."
        if (phone.trim() && phone.replace(/\D/g, '').length < 10) found.phone = "Please check the phone number, or leave it empty."
        if (!topic) found.topic = "Please pick what your message is about."
        if (message.trim().length < 10) found.message = "Please add a few more words so we can help."

        setErrors(found)

        if (Object.keys(found).length) return

        setStatus('submitting')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, phone, topic, message, page: '/contact', website })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean } | null

            if (!response.ok || !data?.ok) throw new Error('Request failed')

            setStatus('done')
        }
        catch {
            setStatus('error')
        }
    }

    const reset = () => {
        setName('')
        setEmail('')
        setPhone('')
        setTopic('')
        setMessage('')
        setErrors({})
        setStatus('idle')
    }

    if (status === 'done') {
        return (
            <div role='status' className='flex min-h-[32rem] flex-col items-start justify-center rounded-[32px] border border-ink/[0.08] bg-paper p-7 sm:p-10'>
                <span className='flex size-12 items-center justify-center rounded-full bg-moss text-white'>
                    <Check className='size-6' aria-hidden='true' />
                </span>

                <h2 className='mt-8 font-heading text-[2rem] font-semibold leading-tight tracking-[-0.035em] text-ink'>
                    Thanks, {name.split(' ')[0]}. Your message is with us.
                </h2>

                <p className='mt-4 max-w-md text-[16px] leading-relaxed text-slate'>
                    We reply to <span className='font-medium text-ink'>{email}</span> the same day during office hours. If it turns urgent, call{' '}
                    <a href={siteConfig.phoneHref} className='font-mono text-ink underline underline-offset-4 hover:text-ember'>{siteConfig.phone}</a>.
                </p>

                <Button type='button' variant='outline' icon='none' className='mt-10' onClick={reset}>
                    Send another message
                </Button>
            </div>
        )
    }

    return (
        <form
            onSubmit={handleSubmit}
            noValidate
            className='relative rounded-[32px] border border-ink/[0.08] bg-paper p-6 shadow-[0_40px_80px_-56px_rgba(21,23,26,0.45)] sm:p-9'
        >
            <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                <label>
                    Website
                    <input tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
                </label>
            </div>

            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-slate'>Send us a message</p>

            <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                <TextField
                    label="Your name"
                    value={name}
                    onChange={setName}
                    autoComplete='name'
                    error={errors.name}
                    maxLength={contactLimits.name}
                />

                <TextField
                    label="Email"
                    type='email'
                    value={email}
                    onChange={setEmail}
                    inputMode='email'
                    autoComplete='email'
                    error={errors.email}
                    maxLength={contactLimits.email}
                />

                <div className='sm:col-span-2'>
                    <TextField
                        label="Phone"
                        type='tel'
                        value={phone}
                        onChange={setPhone}
                        inputMode='tel'
                        autoComplete='tel'
                        error={errors.phone}
                        hint="Add it if you'd rather we call back."
                        optional
                        maxLength={contactLimits.phone}
                    />
                </div>
            </div>

            <div className='mt-7'>
                <p id={topicId} className='text-sm font-medium text-ink'>What is it about?</p>

                <div className='mt-3'>
                    <OptionGrid
                        options={contactTopics}
                        value={topic}
                        onChange={value => setTopic(value)}
                        labelledBy={topicId}
                        invalid={Boolean(errors.topic)}
                    />
                </div>

                {errors.topic && <p className='mt-1.5 text-[13px] text-ember'>{errors.topic}</p>}
            </div>

            <div className='mt-7'>
                <label htmlFor={messageId} className='flex items-baseline justify-between text-sm font-medium text-ink'>
                    Your message
                    <span className='font-mono text-[12px] font-normal text-slate'>{message.length}/{contactLimits.message}</span>
                </label>

                <textarea
                    id={messageId}
                    value={message}
                    onChange={event => setMessage(event.target.value)}
                    rows={5}
                    maxLength={contactLimits.message}
                    placeholder="Tell us what's on your mind. The more detail, the better our answer."
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? `${messageId}-error` : undefined}
                    className={cn(
                        'mt-2 w-full resize-y rounded-2xl border bg-card px-4 py-3 text-[16px] leading-relaxed text-ink outline-none transition-colors placeholder:text-slate/55 focus:border-ember',
                        errors.message ? 'border-ember' : 'border-ink/15'
                    )}
                />

                {errors.message && <p id={`${messageId}-error`} className='mt-1.5 text-[13px] text-ember'>{errors.message}</p>}
            </div>

            <div className='mt-8 flex flex-col gap-4 border-t border-ink/[0.08] pt-7 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-[13px] leading-relaxed text-slate'>
                    We only use your details to answer this message.
                </p>

                <Button
                    type='submit'
                    disabled={status === 'submitting'}
                    icon={status === 'submitting' ? LoaderCircle : Send}
                    className='w-full justify-between sm:w-auto'
                >
                    {status === 'submitting' ? "Sending" : "Send message"}
                </Button>
            </div>

            {status === 'error' && (
                <p role='alert' className='mt-5 text-[14px] text-ember'>
                    Your message didn't reach us. Please try again, or call {siteConfig.phone}.
                </p>
            )}
        </form>
    )
}
